import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import imgProductUsb from "@/assets/images/x86/product-usb.png";
import imgButtonDisc from "@/assets/images/x86/button-disc.png";
import { handleBtn } from "@/pages/freeDownload/interactive";
import { getServiceVal } from "@/utils/cookies";
import { DESKTOP_X86_TRIAL_V500_KEY } from "@/utils/base";
import { findDesktopPackageByDownloadKey, getAllDesktopDownloadGroups } from "@/utils/desktopDownloadPackage";
import { goX86DeveloperCartFlow, goX86DeveloperLogin } from "@/utils/common";
import {
  bindX86DownloadIntentAfterLogin,
  consumeAuthorizationTokenFromUrl,
} from "@/utils/desktopDownloadReport";

import "./index.less";

const prefix = "x86-obtain-page";

/** KaihongOS 桌面版（x86）试用版系统安装 U 盘商品页 */
const USB_CART_URL =
  "https://mall.kaihong.com/productDetail?skuId=2104491704429084674&goodsId=2104491703829299201";

function X86Obtain() {
  const userData = useSelector((state: any) => state.userData);
  const intentBindTriggeredRef = useRef(false);

  const desktopPackage = useMemo(
    () =>
      findDesktopPackageByDownloadKey(
        getAllDesktopDownloadGroups(),
        DESKTOP_X86_TRIAL_V500_KEY,
      ),
    [],
  );

  useEffect(() => {
    const prev = document.title;
    document.title = "KaihongOS桌面版（x86）试用版系统安装U盘";
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    consumeAuthorizationTokenFromUrl();
    if (!getServiceVal("Authorization")) return;
    if (intentBindTriggeredRef.current) return;
    intentBindTriggeredRef.current = true;
    void bindX86DownloadIntentAfterLogin().then((ok) => {
      if (!ok) {
        intentBindTriggeredRef.current = false;
      }
    });
  }, []);

  /** 完全复用原「立即下载」：登录引流 + handleBtn（含 save / 限次 / 实际下载） */
  const onDownload = () => {
    const key = desktopPackage?.download ?? DESKTOP_X86_TRIAL_V500_KEY;
    if (!getServiceVal("Authorization")) {
      void goX86DeveloperLogin(undefined, key);
      return;
    }
    handleBtn(key, "", userData?.userId);
  };

  /** 前往购物车：复用 x86 引流意向，本页不拦登录，直接进商城 */
  const onCart = () => {
    const key = desktopPackage?.download ?? DESKTOP_X86_TRIAL_V500_KEY;
    void goX86DeveloperCartFlow(key, USB_CART_URL);
  };

  return (
    <div className={prefix}>
      <header className={`${prefix}__header`}>
        <div className={`${prefix}__container ${prefix}__headerInner`}>
          <h1 className={`${prefix}__brand`}>
            KaihongOS桌面版（x86）试用版安装路径
          </h1>
        </div>
      </header>

      <main className={`${prefix}__container`}>
        <section className={`${prefix}__hero`} aria-labelledby="x86-obtain-hero-title">
          <div className={`${prefix}__heroCopy`}>
            <h2 id="x86-obtain-hero-title" className={`${prefix}__heroTitle`}>
              自行下载刻录安装  or  免下载免刻录安装
            </h2>
            <p className={`${prefix}__heroSub`}>
              同一系统镜像，两种获取方式，按你的需要选择。
            </p>
          </div>
          <div className={`${prefix}__productSpeak`} aria-hidden="true">
            <div className={`${prefix}__productArt`}>
              <img src={imgProductUsb} alt="" draggable={false} />
            </div>
            <span className={`${prefix}__productCaption`}>
              <span className={`${prefix}__productCaptionAsk`}>
                不会刻盘？嫌太麻烦？
              </span>
            <span className={`${prefix}__productCaptionHit`}>
              一个U盘全搞定！
            </span>
            </span>
          </div>
        </section>

        <section className={`${prefix}__choices`} aria-label="选择获取方式">
          <article className={`${prefix}__choice`}>
            <div className={`${prefix}__choiceHeading`}>
              <span className={`${prefix}__choiceIcon`} aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M9 2h6v9h3.5L12 17.5 5.5 11H9V2Zm-5 16h16v4H4v-4Z" />
                </svg>
              </span>
              <span>自行刻录</span>
            </div>
            <h3 className={`${prefix}__choiceTitle`}>下载镜像包</h3>
            <p className={`${prefix}__choiceDesc`}>
              自行下载、校验与刻录系统安装镜像。
            </p>
            <p className={`${prefix}__choiceWarn`} role="note">
              <span className={`${prefix}__choiceWarnMark`} aria-hidden="true">
                *
              </span>
              <span>
                暂不支持迅雷等工具下载，推荐在浏览器内完成下载。
              </span>
            </p>
            <button
              className={`${prefix}__primary`}
              type="button"
              onClick={onDownload}
            >
              <span className={`${prefix}__buttonLabel ${prefix}__buttonLabel--underline`}>
                立即下载
              </span>
              <img
                className={`${prefix}__buttonDisc`}
                src={imgButtonDisc}
                alt=""
                aria-hidden="true"
                draggable={false}
              />
            </button>
          </article>

          <article className={`${prefix}__choice`}>
            <div className={`${prefix}__choiceHeading`}>
              <span className={`${prefix}__choiceIcon`} aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M2 3h3l2.2 10.1A2 2 0 0 0 9.2 15h9.9a2 2 0 0 0 1.9-1.4L23 7H6l-.5-2H2V3Zm7.5 14a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4Z" />
                </svg>
              </span>
              <span>购买U盘</span>
            </div>
            <h3 className={`${prefix}__choiceTitle`}>免下载 · 免刻录</h3>
            <p className={`${prefix}__choiceDesc`}>
              无需镜像下载、校验与刻录的繁琐流程，到货后直接安装。
            </p>
            <button
              className={`${prefix}__primary`}
              type="button"
              onClick={onCart}
            >
              <span className={`${prefix}__buttonLabel ${prefix}__buttonLabel--underline`}>
                前往购物车
              </span>
              <svg
                className={`${prefix}__buttonHand`}
                viewBox="-2 -12 66 76"
                aria-hidden="true"
              >
                <path d="M21 38V12a5 5 0 0 1 10 0v20-7a4 4 0 0 1 8 0v10-7a4 4 0 0 1 8 0v9-5a4 4 0 0 1 8 0v10c0 9-6 15-15 15H30c-6 0-9-2-13-6L6 42a5 5 0 0 1 7-7l8 8" />
                <path d="M26 -8v4M11 -1l4 4M41 -1l-4 4" />
              </svg>
            </button>
          </article>
        </section>

        <section
          className={`${prefix}__compare`}
          aria-labelledby="x86-obtain-compare-title"
        >
          <h2 id="x86-obtain-compare-title" className={`${prefix}__compareTitle`}>
            获取方式对比
          </h2>
          <div className={`${prefix}__tableWrap`}>
            <table className={`${prefix}__table`}>
              <colgroup>
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" />
                  <th scope="col">自行刻录</th>
                  <th scope="col">购买U盘</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">镜像来源</th>
                  <td>官方最新系统镜像（免费）</td>
                  <td>官方最新系统镜像（免费）</td>
                </tr>
                <tr>
                  <th scope="row">获得内容</th>
                  <td>未刻录的系统原始镜像文件</td>
                  <td className={`${prefix}__tableEmph`}>
                    U盘+预刻录好的系统安装文件
                  </td>
                </tr>
                <tr>
                  <th scope="row">准备工作</th>
                  <td>自行下载镜像并制作系统安装盘</td>
                  <td className={`${prefix}__tableEmph`}>无</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className={`${prefix}__note`}>
            <span className={`${prefix}__noteIcon`} aria-hidden="true">
              i
            </span>
            <span>
              不包含商业授权License，仅供个人体验使用，商业用途请另行申请官方授权
            </span>
          </p>
        </section>
      </main>
    </div>
  );
}

export default X86Obtain;
